import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { PaginationControls } from '../../components/PaginationControls';
import {
  Activity,
  Search,
  Filter,
  ArrowRight,
  Clock,
  User,
  FileText,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const ACTION_COLORS = {
  amend: 'bg-amber-100 text-amber-800',
  update: 'bg-blue-100 text-blue-800',
  approve: 'bg-emerald-100 text-emerald-800',
  reject: 'bg-red-100 text-red-800',
  lock: 'bg-purple-100 text-purple-800',
  create: 'bg-green-100 text-green-800',
  delete: 'bg-red-200 text-red-900',
};

const ENTITY_ICONS = {
  order: '&#128230;',
  part: '&#9881;',
  quotation: '&#128196;',
  costing: '&#128178;',
};

export default function ActivityLogsPage() {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [filters, setFilters] = useState({
    entity_type: 'all',
    action: 'all',
    search: '',
  });
  const [selectedLog, setSelectedLog] = useState(null);
  const [summary, setSummary] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (filters.entity_type !== 'all') params.append('entity_type', filters.entity_type);
      if (filters.action !== 'all') params.append('action', filters.action);
      
      const res = await axios.get(`${API_URL}/activity-logs?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLogs(res.data.logs);
      setTotal(res.data.total);
      setPages(res.data.pages);
    } catch (err) {
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  }, [token, page, filters]);

  const fetchSummary = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/activity-logs/summary?days=7`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSummary(res.data);
    } catch (err) {
      // silent
    }
  }, [token]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);
  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const truncate = (str, len = 60) => {
    if (!str) return '-';
    const s = String(str);
    return s.length > len ? s.substring(0, len) + '...' : s;
  };

  return (
    <div className="space-y-6" data-testid="activity-logs-page">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="activity-logs-title">Activity Logs</h1>
          <p className="text-slate-500 mt-1">
            Field-level change tracking for all amendments and updates.
          </p>
        </div>
        <Button variant="outline" onClick={() => { fetchLogs(); fetchSummary(); }} data-testid="refresh-activity-logs">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Total Changes (7d)</p>
              <p className="text-2xl font-bold mt-1" data-testid="total-changes-7d">
                {summary.activity_breakdown?.reduce((s, r) => s + r.count, 0) || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Active Users (7d)</p>
              <p className="text-2xl font-bold mt-1">{summary.top_users?.length || 0}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Amendments</p>
              <p className="text-2xl font-bold mt-1 text-amber-600">
                {summary.activity_breakdown?.filter(r => r.action === 'amend').reduce((s, r) => s + r.count, 0) || 0}
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <p className="text-xs text-muted-foreground uppercase">Most Active</p>
              <p className="text-lg font-semibold mt-1 truncate">
                {summary.top_users?.[0]?.user_name || '-'}
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap gap-3 items-center">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <Select value={filters.entity_type} onValueChange={(v) => { setFilters(f => ({...f, entity_type: v})); setPage(1); }}>
              <SelectTrigger className="w-40" data-testid="filter-entity-type">
                <SelectValue placeholder="Entity Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="order">Sales Orders</SelectItem>
                <SelectItem value="part">Parts</SelectItem>
                <SelectItem value="quotation">Quotations</SelectItem>
                <SelectItem value="costing">Costings</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filters.action} onValueChange={(v) => { setFilters(f => ({...f, action: v})); setPage(1); }}>
              <SelectTrigger className="w-36" data-testid="filter-action">
                <SelectValue placeholder="Action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="amend">Amend</SelectItem>
                <SelectItem value="update">Update</SelectItem>
                <SelectItem value="approve">Approve</SelectItem>
                <SelectItem value="reject">Reject</SelectItem>
                <SelectItem value="lock">Lock</SelectItem>
              </SelectContent>
            </Select>
            <span className="text-sm text-muted-foreground ml-auto">
              {total} record{total !== 1 ? 's' : ''} found
            </span>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Timestamp</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Action</TableHead>
                <TableHead>Changes</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="w-[80px]">Details</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">Loading...</TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No activity logs found. Changes will appear here when users amend or update records.
                  </TableCell>
                </TableRow>
              ) : logs.map((log) => (
                <TableRow key={log.id} className="hover:bg-slate-50 cursor-pointer" onClick={() => setSelectedLog(log)}>
                  <TableCell className="text-xs text-muted-foreground">
                    <Clock className="w-3 h-3 inline mr-1" />
                    {formatDate(log.timestamp)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <User className="w-3 h-3 text-muted-foreground" />
                      <span className="text-sm font-medium">{log.user_name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <Badge variant="outline" className="text-xs capitalize">{log.entity_type}</Badge>
                      <span className="text-xs text-muted-foreground ml-1 font-mono">{truncate(log.entity_ref, 20)}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge className={ACTION_COLORS[log.action] || 'bg-gray-100 text-gray-700'}>
                      {log.action}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-muted-foreground">
                      {log.changes?.length || 0} field{(log.changes?.length || 0) !== 1 ? 's' : ''} changed
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs">{truncate(log.amendment_reason, 30) || '-'}</span>
                  </TableCell>
                  <TableCell>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setSelectedLog(log); }} data-testid={`view-log-${log.id}`}>
                      <FileText className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      {pages > 1 && (
        <PaginationControls
          currentPage={page}
          totalPages={pages}
          onPageChange={setPage}
        />
      )}

      {/* Detail Modal */}
      <Dialog open={!!selectedLog} onOpenChange={() => setSelectedLog(null)}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="activity-log-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="h-5 w-5 text-blue-600" />
              Change Details
            </DialogTitle>
          </DialogHeader>
          {selectedLog && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">User:</span>
                  <span className="ml-2 font-medium">{selectedLog.user_name}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Time:</span>
                  <span className="ml-2">{formatDate(selectedLog.timestamp)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Entity:</span>
                  <Badge variant="outline" className="ml-2 capitalize">{selectedLog.entity_type}</Badge>
                  <span className="ml-1 font-mono text-xs">{selectedLog.entity_ref}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Action:</span>
                  <Badge className={`ml-2 ${ACTION_COLORS[selectedLog.action] || ''}`}>{selectedLog.action}</Badge>
                </div>
              </div>

              {selectedLog.amendment_reason && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm font-medium text-amber-800">Amendment Reason</p>
                  <p className="text-sm mt-1">{selectedLog.amendment_reason}</p>
                </div>
              )}

              {selectedLog.changes?.length > 0 && (
                <div>
                  <h4 className="font-semibold text-sm mb-2">Field Changes</h4>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Field</TableHead>
                        <TableHead>Old Value</TableHead>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>New Value</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedLog.changes.map((change, idx) => (
                        <TableRow key={change.field || `change-${idx}`}>
                          <TableCell className="font-mono text-xs font-medium">{change.field}</TableCell>
                          <TableCell className="text-xs text-red-600 max-w-[200px] break-all">{truncate(change.old_value, 100) || <span className="italic text-muted-foreground">empty</span>}</TableCell>
                          <TableCell><ArrowRight className="w-3 h-3 text-muted-foreground" /></TableCell>
                          <TableCell className="text-xs text-emerald-600 max-w-[200px] break-all">{truncate(change.new_value, 100) || <span className="italic text-muted-foreground">empty</span>}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
